import { Trans } from "@lingui/react/macro";
import React from "react";
import {
  LocationSpecificMainMenuItemFragment,
  MainMenuSectionFragment
} from "types/graphql-types";
import { MenuItemContentProps } from "./links/MenuItem";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import { Geolocation } from "src/utils/localization/geolocation/geolocation";
import { match } from "ts-pattern";
import { toLink } from "./links/GenericLinks";
import { isPresent } from "src/utils/checks";
import { ValidatedMainMenuItem } from "../utils";
import * as styles from "./TrendingNow.module.scss";
import MenuItemIconOrImage from "./links/MenuItemIconOrImage";
import { ImageRatio } from "@silversea-ssc/ui-components-react";
import {
  MenuPromosProviderContextValue,
  useMenuPromos
} from "../MenuPromosProvider";
import { toPromoLink } from "./links/PromoLinks";
import { byConfigurationCode } from "src/utils/promos";

type Props = MenuItemContentProps;

const TrendingNow: React.FC<Props> = ({ image, label, href, content }) => {
  return (
    <div className={styles.trending} data-content-name="trending-now">
      <div className={styles.title}>
        <Trans id="trending-title">Trending Now</Trans>
      </div>

      <a
        role="menuitem"
        href={href}
        className={styles.link}
        data-content-piece={`trending-now-link`}
      >
        <ImageRatio ratio="card-compact">
          <MenuItemIconOrImage image={image} label={label} variant="trending" />
        </ImageRatio>
        <div>
          <div className={styles.label}>{label}</div>
          {content && <div>{content}</div>}
        </div>
      </a>
    </div>
  );
};

export const useTrendingNowProps = (
  items: NonNullable<MainMenuSectionFragment["trendingNow"]>
): MenuItemContentProps | undefined => {
  const geolocation = useGeolocation();
  const activePromos = useMenuPromos();
  const item = items[0];
  if (!item) return undefined;
  const props = toProps(item, activePromos, geolocation);
  if (!props) return undefined;

  return props;
};

const toProps = (
  item: NonNullable<MainMenuSectionFragment["trendingNow"]>[number],
  promos: MenuPromosProviderContextValue,
  geolocation: Geolocation
): MenuItemContentProps | undefined => {
  if (!item) return undefined;

  return match(item)
    .with({ _type: "specialVoyage" }, (i) => {
      if (!isPresent(i?.fullPath)) return undefined;

      return {
        _key: i._id,
        href: i.fullPath!,
        label: i.card?.title?.localized || "",
        image: i.card?.image || undefined,
        content: i.card?.description?.localized || "",
        trackingName: i.metadata?.tracking?.name
      };
    })
    .with(
      { __typename: "SanityLocationSpecificMainMenuItem" },
      { __typename: "SanityMainMenuItem" },
      (i) => {
        const realItem = i as LocationSpecificMainMenuItemFragment;
        if (isPresent(realItem.page?.fullPath))
          return toLink(true, geolocation)(realItem as ValidatedMainMenuItem);
      }
    )

    .with({ __typename: "SanityExclusiveOfferPerPromo" }, (i) => {
      const targetPromo = promos.find(
        byConfigurationCode(i.configuration?.code)
      );
      if (!targetPromo) return undefined;
      const props = toPromoLink(targetPromo);
      return { ...props, image: i.card?.image };
    })
    .otherwise(() => undefined);
};

export default TrendingNow;
